import { useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch } from "lucide-react";
import { api, money, imgSrc } from "@/lib/api";
import { StatusPill } from "@/pages/Account";
import { MaskLines } from "@/components/Reveal";

const STEPS = ["pending", "paid", "shipped", "delivered"];
const stepOf = (status) => Math.max(0, STEPS.indexOf(status));

const Timeline = ({ status }) => {
  const cur = stepOf(status);
  return (
    <div className="mt-8 flex items-center gap-0" data-testid="order-timeline">
      {STEPS.map((s, i) => (
        <div key={s} className={`flex ${i < STEPS.length - 1 ? "flex-1" : ""} items-center`}>
          <div className="flex flex-col items-center">
            <span className={`flex h-9 w-9 items-center justify-center rounded-full font-mono text-xs ${i <= cur && status !== "cancelled" ? "bg-ink text-bone" : "border border-line text-stone"}`}>
              {i + 1}
            </span>
            <span className="mt-2 font-mono text-[10px] uppercase tracking-widest text-stone">{s}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`mx-2 mb-5 h-px flex-1 md:mx-4 ${i < cur && status !== "cancelled" ? "bg-ink" : "bg-line"}`} />}
        </div>
      ))}
    </div>
  );
};

export default function TrackOrder() {
  const [form, setForm] = useState({ order_id: "", email: "" });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const { data } = await api.post("/orders/lookup", form);
      setOrder(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main data-testid="track-order-page" className="mx-auto max-w-3xl px-5 pb-32 pt-28 md:pt-36">
      <p className="eyebrow">Order tracking</p>
      <h1 className="mt-4 font-display text-6xl font-light leading-none md:text-7xl">
        <MaskLines lines={["Where's my", "order?"]} lineClass="last:italic" />
      </h1>
      <form onSubmit={lookup} className="mt-10 space-y-5" data-testid="track-order-form">
        <label className="block" htmlFor="order_id">
          <span className="eyebrow">Order number</span>
          <input id="order_id" required value={form.order_id} onChange={set("order_id")} placeholder="GC-XXXXXXXX" data-testid="track-order-input" className="mt-2 w-full border-b border-ink/40 bg-transparent py-3 font-mono text-lg uppercase outline-none transition-colors focus:border-ink placeholder:text-stone/60" />
        </label>
        <label className="block" htmlFor="track_email">
          <span className="eyebrow">Email used at checkout</span>
          <input id="track_email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="track-email-input" className="mt-2 w-full border-b border-ink/40 bg-transparent py-3 text-lg outline-none transition-colors focus:border-ink placeholder:text-stone/60" />
        </label>
        {error && <p data-testid="track-error" className="text-sm text-signal">{error}</p>}
        <button type="submit" disabled={loading} data-testid="track-submit-button" className="flex w-full items-center justify-center gap-3 rounded-full bg-ink py-4 text-sm text-bone transition-colors duration-300 hover:bg-signal disabled:opacity-60">
          <PackageSearch className="h-4 w-4" />{loading ? "Looking…" : "Track my order"}
        </button>
      </form>

      {order && (
        <section data-testid="order-track-result" className="mt-14 border border-line bg-white p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-3xl">{order.order_id}</p>
              <p className="mt-1 text-sm text-stone">Placed {new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
            <StatusPill status={order.status} testid="track-order-status" />
          </div>
          {order.status !== "cancelled" && <Timeline status={order.status} />}
          <ul className="mt-8 divide-y divide-line border-y border-line">
            {(order.items || []).map((it) => (
              <li key={`${it.product_id}-${it.size}`} className="flex items-center gap-4 py-4">
                <img src={imgSrc(it.image)} alt={it.name} className="h-16 w-14 object-cover" />
                <div className="flex flex-1 justify-between">
                  <div>
                    <p className="font-display text-xl">{it.name}</p>
                    <p className="eyebrow mt-1">{it.size} × {it.quantity}</p>
                  </div>
                  <span className="font-mono text-sm">{money(it.price * it.quantity)}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-between">
            <span className="font-display text-2xl">Total {order.promo ? <em className="text-base not-italic text-stone">(code {order.promo})</em> : null}</span>
            <span data-testid="track-order-total" className="font-mono">{money(order.total)}</span>
          </div>
          {order.tracking_number && (
            <p className="mt-4 border border-line bg-sand p-3 text-sm" data-testid="track-order-tracking">
              Tracking number — <strong className="font-mono">{order.tracking_number}</strong>
            </p>
          )}
          {order.payment_status !== "paid" && (
            <p className="mt-4 text-sm text-stone">Payment for this order hasn't gone through yet.</p>
          )}
        </section>
      )}
      <p className="mt-10 text-sm text-stone">Ordered while signed in? Your orders also live on your <Link to="/account" className="link-underline text-ink" data-testid="track-account-link">account page</Link>.</p>
    </main>
  );
}
